import React, { useCallback, useEffect, useRef, useState } from 'react';
import { UI } from './ui';
import * as formService from '../../../form-services/presentation-editor';
import { EndingSpecMode } from '../../../entities/ending-spec-mode';
import { getNowUnixTimestamp, getUtcNowUnixTimestamp, MINUTE_IN_MILLISECONDS, SECOND_IN_MILLISECONDS, toDate, toUtcUnixTimestamp } from '../../../../../framework/utils/date-time';
import { ITimingSpecComponentModel } from '../../../../markdown/parser/timing-spec';
import { where } from '../../../../../framework/utils/collection';
import { TimingMode } from '../../../../../entities/timing-mode';
import { IPresenterAccountPreferences } from '../../../../../entities-generated/common/presenter-account-preferences';
import { TimeDisplayFormat } from '../../../../../entities-generated/common/time-display-format';
import { browserUses12HourTime } from '../../../../../framework/utils/device';

interface IPresentationPreflightProps {
  onReadyToFly: () => void;
  accountPreferences: IPresenterAccountPreferences;
}

const subscriberId = 'Section.PresentationPreflight';

export const PresentationPreflight: React.FunctionComponent<IPresentationPreflightProps> = (props) => {
  const formModel = formService.useFormModel(subscriberId);
  const formModelRef = useRef(formModel);
  const [durationOffsetEndTime, setDurationOffsetEndTime] = useState(0);
  const endTimeUpdateCancellation = useRef<number | undefined>(undefined);

  useEffect(() => {
    formModelRef.current = formModel;
  }, [formModel]);

  const updateOffsetTime = useCallback(() => {
    const now = getNowUnixTimestamp();
    const fm = formModelRef.current;
    const nextMinDelta = MINUTE_IN_MILLISECONDS - (now % MINUTE_IN_MILLISECONDS);
    const endTime = now + fm.duration! * SECOND_IN_MILLISECONDS;
    setDurationOffsetEndTime(endTime);

    endTimeUpdateCancellation.current = window.setTimeout(updateOffsetTime, nextMinDelta);
  }, []);

  useEffect(() => {
    const fm = formModelRef.current;
    if(fm.endingSpecMode === EndingSpecMode.DurationOffset) {
      updateOffsetTime();
    }

    return () => {
      if(endTimeUpdateCancellation.current !== undefined) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        window.clearTimeout(endTimeUpdateCancellation.current);
      }
    }
  }, [updateOffsetTime]);

  const onUseDurationOffset = () => {
    formService.endingSpecMode.onUpdate(EndingSpecMode.DurationOffset);
    updateOffsetTime();
  };

  const onSpecifyEndTime = () => {
    console.log(formModel);
    formService.endingSpecMode.onUpdate(EndingSpecMode.ExplicitlyDefined);
    if(endTimeUpdateCancellation.current !== undefined) {
      window.clearTimeout(endTimeUpdateCancellation.current);
      endTimeUpdateCancellation.current = undefined;
    }
  };

  const onStartPresentation = () => {
    // determine and note current section
    const allSections = formModel.componentModel as ITimingSpecComponentModel[];
    const selectableSections = where(allSections, s => s.specType !== TimingMode.Preamble);
    const currentSection = selectableSections.length > 0 ? selectableSections[0] : formService.currentSection.getDefaultValue();
    formService.currentSection.setInputValue(currentSection);

    // note start time
    const startTime = getUtcNowUnixTimestamp();
    formService.currentSectionStart.setInputValue(startTime);

    // compute and note presentation end time
    const endTime = formModel.endingSpecMode === EndingSpecMode.DurationOffset ?
      startTime + (formModel.duration! * SECOND_IN_MILLISECONDS) : toUtcUnixTimestamp(formModel.presenterDefinedEndTime.value);
    formService.endTime.setInputValue(endTime);

    // note actual duration
    const presentationDuration = endTime - startTime;
    formService.actualDuration.setInputValue(presentationDuration);

    formService.validateAndUpdateModel()
    .then(() => {
      props.onReadyToFly();
    })
    .catch(() => {}); // for completeness only; no errors possible here
  };

  const twelveHourTimeDisplay = props.accountPreferences.timeDisplayFormat !== TimeDisplayFormat.ClientDefault ?
    props.accountPreferences.timeDisplayFormat === TimeDisplayFormat.Hour12 : browserUses12HourTime();

  return (
    <UI
      formModel={formModel}
      onUseDurationOffset={onUseDurationOffset}
      onSpecifyEndTime={onSpecifyEndTime}
      onStartPresentation={onStartPresentation}
      durationOffsetEndTime={toDate(durationOffsetEndTime)}
      twelveHourTimeDisplay={twelveHourTimeDisplay}
    />
  );
};
